import { usePocMutation } from 'core/custom-hooks/use-poc-mutation.hook';
import { useTranslation } from 'core/i18n/i18n';
import { materialDefinitionKeys } from 'core/query-client/keys';
import { updateQuery } from 'core/query-client/query-client';
import { showErrorNotification, showSuccessNotification } from 'core/utils/notification-service';
import { classifyMaterialDefinitionApiError } from 'modules/settings/pods/settings-materials/service/material-definitions/material-definitions.business';
import {
    createMaterialDefinition,
    updateMaterialDefinition,
} from 'modules/settings/pods/settings-materials/service/material-definitions/material-definitions.service';
import { MaterialDefinitionFormState } from '../settings-material-definitions-form.model';

type SaveMode = 'create' | 'update';

interface UseMaterialDefinitionSaverProps {
    onSuccess: VoidFunction;
}

interface UpdatePayload {
    id: string;
    formState: MaterialDefinitionFormState;
}

export const useMaterialDefinitionSaver = ({ onSuccess }: UseMaterialDefinitionSaverProps) => {
    const { t } = useTranslation();

    const successKey = (mode: SaveMode): string =>
        mode === 'create'
            ? 'settings.materials.material-definitions.form.notifications.create-success'
            : 'settings.materials.material-definitions.form.notifications.update-success';

    const errorKey = (mode: SaveMode, error: unknown): string => {
        switch (classifyMaterialDefinitionApiError(error)) {
            case 'immutable-rules':
                return 'settings.materials.material-definitions.form.notifications.lot-rules-immutable-error';
            case 'result-type-immutable':
                return 'settings.materials.material-definitions.form.notifications.result-type-immutable-error';
            case 'range-mode-immutable':
                return 'settings.materials.material-definitions.form.notifications.range-mode-immutable-error';
            case 'qc-validation':
                return 'settings.materials.material-definitions.form.notifications.qc-validation-error';
            case 'duplicate':
                return 'settings.materials.material-definitions.form.notifications.duplicate-error';
            default:
                return mode === 'create'
                    ? 'settings.materials.material-definitions.form.notifications.create-error'
                    : 'settings.materials.material-definitions.form.notifications.update-error';
        }
    };

    const handleSuccess = (mode: SaveMode) => {
        updateQuery(materialDefinitionKeys.all);
        showSuccessNotification(t(successKey(mode)));
        onSuccess();
    };

    const handleError = (mode: SaveMode, error: unknown) => {
        showErrorNotification(t(errorKey(mode, error)));
    };

    const createMutation = usePocMutation({
        mutationFn: createMaterialDefinition,
        onSuccess: () => handleSuccess('create'),
        onError: (error) => handleError('create', error),
    });

    const updateMutation = usePocMutation({
        mutationFn: ({ id, formState }: UpdatePayload) => updateMaterialDefinition(id, formState),
        onSuccess: () => handleSuccess('update'),
        onError: (error) => handleError('update', error),
    });

    return {
        create: (formState: MaterialDefinitionFormState) => createMutation.mutate(formState),
        update: (id: string, formState: MaterialDefinitionFormState) => updateMutation.mutate({ id, formState }),
        isSaving: createMutation.isPending || updateMutation.isPending,
    };
};
